import EncryptedStorage from 'react-native-encrypted-storage';
import { Platform } from 'react-native';

const KEY = 'vr_chat_muted_prefs_v1';

/**
 * Заглушение чата на устройстве: `true` — навсегда, число — до этого момента (мс эпохи).
 *
 * <p>Главная копия настройки — на сервере (волна 3, C1): он не шлёт пуш заглушённым. Здесь —
 * её отражение для того, что решается на устройстве без сервера: звук входящего по сокету при
 * открытом приложении и уведомление, нарисованное самим приложением. Список чатов переписывает
 * это отражение из ответа сервера на каждой загрузке, поэтому оно не расходится с другими
 * устройствами дольше, чем до следующего открытия списка.
 *
 * <p>Срок (аудит удобства 2026-10-04) хранится числом рядом со старыми `true`: записи прежних
 * версий читаются как «навсегда», то есть как и задумывались.
 */
type MuteValue = true | number;

function isWeb(): boolean {
  return Platform.OS === 'web';
}

function normalizeMuted(raw: unknown): Record<string, MuteValue> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, MuteValue> = {};
  for (const [chatId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!chatId) continue;
    if (value === true) out[chatId] = true;
    else if (typeof value === 'number' && Number.isFinite(value) && value > 0) out[chatId] = value;
  }
  return out;
}

function activeAt(value: MuteValue | undefined, now: number): boolean {
  if (value === true) return true;
  return typeof value === 'number' && value > now;
}

// In-memory snapshot, чтобы горячий путь WS onmessage (доставка сообщений) не делал
// дисковый I/O на каждое входящее сообщение. Заполняется при первом чтении и
// синхронизируется при каждой записи через writeChatMutedPrefs / writeChatMuteStates.
let mutedSnapshot: Record<string, MuteValue> | null = null;
let snapshotPromise: Promise<Record<string, MuteValue>> | null = null;

async function loadFromStorage(): Promise<Record<string, MuteValue>> {
  try {
    let raw: string | null = null;
    if (isWeb()) {
      raw = globalThis.localStorage?.getItem(KEY) ?? null;
    } else {
      raw = await EncryptedStorage.getItem(KEY);
    }
    if (!raw) return {};
    return normalizeMuted(JSON.parse(raw));
  } catch {
    return {};
  }
}

async function ensureSnapshot(): Promise<Record<string, MuteValue>> {
  if (mutedSnapshot) return mutedSnapshot;
  if (!snapshotPromise) {
    snapshotPromise = loadFromStorage().then((data) => {
      mutedSnapshot = data;
      snapshotPromise = null;
      return data;
    });
  }
  return snapshotPromise;
}

async function persist(normalized: Record<string, MuteValue>): Promise<void> {
  mutedSnapshot = normalized;
  const payload = JSON.stringify(normalized);
  try {
    if (isWeb()) {
      globalThis.localStorage?.setItem(KEY, payload);
      return;
    }
    await EncryptedStorage.setItem(KEY, payload);
  } catch {
    /* ignore storage write errors */
  }
}

/** Заглушённые сейчас чаты; истёкшие сроки сюда уже не попадают. */
export async function readChatMutedPrefs(): Promise<Record<string, boolean>> {
  const snap = await ensureSnapshot();
  const now = Date.now();
  const out: Record<string, boolean> = {};
  for (const [chatId, value] of Object.entries(snap)) {
    if (activeAt(value, now)) out[chatId] = true;
  }
  return out;
}

/** Старый вид записи — «навсегда» на каждый `true`. */
export async function writeChatMutedPrefs(value: Record<string, boolean>): Promise<void> {
  const only: Record<string, true> = {};
  for (const [chatId, muted] of Object.entries(value ?? {})) {
    if (muted === true) only[chatId] = true;
  }
  await persist(normalizeMuted(only));
}

/**
 * Записать заглушения со сроками: `null` — навсегда, дата — до неё. Целиком, а не по одному:
 * источник правды — список с сервера, и чат, который там больше не заглушён, отсюда тоже
 * обязан пропасть.
 */
export async function writeChatMuteStates(value: Record<string, Date | null>): Promise<void> {
  const raw: Record<string, MuteValue> = {};
  for (const [chatId, until] of Object.entries(value)) {
    raw[chatId] = until == null ? true : until.getTime();
  }
  await persist(normalizeMuted(raw));
}

/**
 * Синхронно отвечает «mute или нет», если снапшот уже загружен (типичный кейс
 * после первого использования). Иначе возвращает false и асинхронно прогревает кэш.
 * Этого достаточно для звукового сигнала на WS-сообщении: false-negative в первые
 * миллисекунды после старта приложения некритичен.
 */
export function isChatMutedSync(chatId: string): boolean {
  if (!chatId) return false;
  if (mutedSnapshot) return activeAt(mutedSnapshot[chatId], Date.now());
  void ensureSnapshot();
  return false;
}

export async function isChatMuted(chatId: string): Promise<boolean> {
  if (!chatId) return false;
  const snap = await ensureSnapshot();
  return activeAt(snap[chatId], Date.now());
}
